import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, Briefcase, Clock, Home, ChevronRight, CheckCircle2, Phone, Mail, MessageCircle, Smartphone, User, CalendarDays, BarChart3, Plus, X, Check } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { useScreenInit } from '../../useScreenInit.js';
import { usePlatform, initials, ContactChannel } from '../../context/PlatformContext';
import { findInterventionId } from './appartamentiData';
const availabilityMeta: Record<string, {
  label: string;
  cls: string;
  dot: string;
}> = {
  disponibile: {
    label: 'Disponibile',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500'
  },
  occupato: {
    label: 'Occupato',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500'
  },
  off: {
    label: 'Off',
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
    dot: 'bg-gray-400'
  }
};
const jobStatusMeta: Record<string, {
  label: string;
  cls: string;
}> = {
  da_assegnare: {
    label: 'Da assegnare',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
const channelMeta: Record<ContactChannel, {
  label: string;
  icon: React.ReactNode;
}> = {
  whatsapp: {
    label: 'WhatsApp',
    icon: <MessageCircle size={14} data-id="element-2591" />
  },
  telefono: {
    label: 'Telefono',
    icon: <Phone size={14} data-id="element-2592" />
  },
  email: {
    label: 'Email',
    icon: <Mail size={14} data-id="element-2593" />
  },
  sms: {
    label: 'SMS',
    icon: <Smartphone size={14} data-id="element-2594" />
  }
};
// Static weekly availability shown in the Disponibilità tab.
const WEEK = [{
  day: 'Lun',
  slot: '08:00 – 18:00',
  on: true
}, {
  day: 'Mar',
  slot: '08:00 – 18:00',
  on: true
}, {
  day: 'Mer',
  slot: '08:00 – 13:00',
  on: true
}, {
  day: 'Gio',
  slot: '08:00 – 18:00',
  on: true
}, {
  day: 'Ven',
  slot: '08:00 – 16:00',
  on: true
}, {
  day: 'Sab',
  slot: 'Su richiesta',
  on: false
}, {
  day: 'Dom',
  slot: 'Riposo',
  on: false
}];
type TabId = 'informazioni' | 'disponibilita' | 'interventi' | 'statistiche';
export function CmTeamCleanerDettaglioPage() {
  useScreenInit();
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    cleanerById,
    jobsForCleaner,
    cleanerAvgScore,
    jobs,
    sendAssignment
  } = usePlatform();
  const [tab, setTab] = useState<TabId>('informazioni');
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const cleaner = cleanerById(id);
  if (!cleaner) {
    return <MobileLayout data-id="element-2595">
        <div className="text-center py-20" data-id="element-2596">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2597">
            Cleaner non trovato.
          </p>
          <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2598">
            <ArrowLeft size={15} data-id="element-2599" />
            Torna al team
          </Link>
        </div>
      </MobileLayout>;
  }
  const jobsCleaner = jobsForCleaner(cleaner.id);
  const attivi = jobsCleaner.filter(j => j.status === 'assegnata' || j.status === 'in_corso');
  const completati = jobsCleaner.filter(j => j.status === 'completata');
  const avg = cleanerAvgScore(cleaner.name);
  const meta = availabilityMeta[cleaner.availability];
  const channels = cleaner.channels ?? [];
  // Accepted opportunities (da_assegnare) assignable to this cleaner:
  // match on zone coverage when possible, otherwise show all open jobs.
  const openJobs = jobs.filter(j => j.status === 'da_assegnare');
  const matchesZone = (zone: string) => cleaner.zones.some(z => zone.toLowerCase().includes(z.toLowerCase()) || z.toLowerCase().includes(zone.toLowerCase()));
  const assignable = openJobs.filter(j => matchesZone(j.zone));
  const otherOpen = openJobs.filter(j => !matchesZone(j.zone));
  const handleAssign = (jobId: string) => {
    sendAssignment(jobId, cleaner.id, cleaner.channels?.[0]);
  };
  const tabItems = [{
    id: 'informazioni',
    label: 'Informazioni',
    icon: <User size={15} data-id="element-2600" />
  }, {
    id: 'disponibilita',
    label: 'Disponibilità',
    icon: <CalendarDays size={15} data-id="element-2601" />
  }, {
    id: 'interventi',
    label: 'Interventi',
    icon: <Briefcase size={15} data-id="element-2602" />,
    badge: jobsCleaner.length > 0 ? <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground" data-id="element-2603">
            {jobsCleaner.length}
          </span> : undefined
  }, {
    id: 'statistiche',
    label: 'Statistiche',
    icon: <BarChart3 size={15} data-id="element-2604" />
  }];
  return <MobileLayout data-id="element-2605">
      <Link to="/cm/team" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2606">
        <ArrowLeft size={16} data-id="element-2607" />
        Team
      </Link>

      {/* Header */}
      <div className="bg-white border border-border rounded-xl shadow-sm p-4 lg:p-5 mb-5" data-id="element-2608">
        <div className="flex items-start gap-4" data-id="element-2609">
          <span className="w-16 h-16 rounded-full bg-coral/10 flex items-center justify-center text-xl font-bold text-coral flex-shrink-0" data-id="element-2610">
            {initials(cleaner.name)}
          </span>
          <div className="flex-1 min-w-0" data-id="element-2611">
            <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2612">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2613">
                {cleaner.name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.cls}`} data-id="element-2614">
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} data-id="element-2615" />
                {meta.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground" data-id="element-2616">
              <MapPin size={14} className="flex-shrink-0" data-id="element-2617" />
              {cleaner.zones.length > 0 ? cleaner.zones.join(', ') : '—'}
            </div>
          </div>
          <Button onClick={() => setIsAssignOpen(true)} className="hidden sm:inline-flex flex-shrink-0" data-id="element-2618">
            <Plus size={16} data-id="element-2619" />
            Assegna
          </Button>
        </div>
        {/* Mobile CTA */}
        <Button onClick={() => setIsAssignOpen(true)} className="w-full mt-4 sm:hidden" data-id="element-2620">
          <Plus size={16} data-id="element-2621" />
          Assegna opportunità
        </Button>
      </div>

      {/* Tabs */}
      <Tabs scrollable className="mb-6" value={tab} onValueChange={t => setTab(t as TabId)} items={tabItems} data-id="element-2622" />

      {/* INFORMAZIONI */}
      {tab === 'informazioni' && <div className="space-y-3" data-id="element-2623">
          <Card data-id="element-2624">
            <CardContent className="space-y-0 divide-y divide-border" data-id="element-2625">
              <InfoRow label="Nome" value={cleaner.name.split(' ')[0]} data-id="element-2626" />
              <InfoRow label="Cognome" value={cleaner.name.split(' ').slice(1).join(' ') || '—'} data-id="element-2627" />
              <InfoRow label="Telefono" value={cleaner.phone ?? '—'} data-id="element-2628" />
              <InfoRow label="Email" value={cleaner.email ?? '—'} data-id="element-2629" />
              <InfoRow label="Zone coperte" value={cleaner.zones.length > 0 ? cleaner.zones.join(', ') : '—'} data-id="element-2630" />
            </CardContent>
          </Card>

          <div data-id="element-2631">
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2" data-id="element-2632">
              Canali di contatto
            </h3>
            {channels.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-2633">
                {channels.map(ch => <span key={ch} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-border text-sm font-medium text-navy" data-id="element-2634">
                    {channelMeta[ch].icon}
                    {channelMeta[ch].label}
                  </span>)}
              </div> : <p className="text-sm text-muted-foreground" data-id="element-2635">
                Nessun canale configurato.
              </p>}
          </div>
        </div>}

      {/* DISPONIBILITÀ */}
      {tab === 'disponibilita' && <Card data-id="element-2636">
          <CardContent className="space-y-0 divide-y divide-border" data-id="element-2637">
            {WEEK.map(d => <div key={d.day} className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-2638">
                <span className="text-sm font-medium text-navy w-12" data-id="element-2639">
                  {d.day}
                </span>
                <span className={`text-sm ${d.on ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-2640">
                  {d.slot}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${d.on ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`} data-id="element-2641">
                  <span className={`w-1.5 h-1.5 rounded-full ${d.on ? 'bg-emerald-500' : 'bg-gray-400'}`} data-id="element-2642" />
                  {d.on ? 'Attivo' : 'Off'}
                </span>
              </div>)}
          </CardContent>
        </Card>}

      {/* INTERVENTI */}
      {tab === 'interventi' && (jobsCleaner.length > 0 ? <div className="space-y-3" data-id="element-2643">
            {jobsCleaner.map(j => {
        const sm = j.status === 'da_assegnare' && j.assignment?.phase === 'inviata' ? {
          label: 'In attesa di risposta',
          cls: 'bg-amber-50 text-amber-700 border-amber-200'
        } : jobStatusMeta[j.status] || jobStatusMeta['da_assegnare'];
        return <Card key={j.id} className="hover:border-coral/40 hover:shadow-sm transition-all cursor-pointer" onClick={() => {
          const interventoId = findInterventionId(j.apartmentId, j.date);
          navigate(interventoId ? `/cm/clienti/appartamenti/${j.apartmentId}/interventi/${interventoId}` : `/cm/clienti/appartamenti/${j.apartmentId}`);
        }} data-id="element-2644">
                  <CardContent className="flex items-center justify-between gap-3" data-id="element-2645">
                    <div className="min-w-0" data-id="element-2646">
                      <div className="flex items-center gap-2 mb-1" data-id="element-2647">
                        <span className="text-sm font-semibold text-foreground truncate" data-id="element-2648">
                          {j.apartmentName}
                        </span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${sm.cls}`} data-id="element-2649">
                          {sm.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap" data-id="element-2650">
                        <span className="flex items-center gap-1" data-id="element-2651">
                          <Home size={12} data-id="element-2652" />
                          {j.type}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-2653">
                          <Clock size={12} data-id="element-2654" />
                          {j.date} · {j.time}
                        </span>
                        <span data-id="element-2655">€{j.earning}</span>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-300 flex-shrink-0" data-id="element-2656" />
                  </CardContent>
                </Card>;
      })}
          </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-2657">
            <p className="text-sm text-muted-foreground" data-id="element-2658">
              Nessun incarico assegnato a {cleaner.name}.
            </p>
          </div>)}

      {/* STATISTICHE */}
      {tab === 'statistiche' && <div className="space-y-5" data-id="element-2659">
          <div className="grid grid-cols-3 gap-3" data-id="element-2660">
            <Card data-id="element-2661">
              <CardContent className="text-center" data-id="element-2662">
                <div className="w-9 h-9 rounded-lg bg-coral/10 text-coral flex items-center justify-center mx-auto mb-2" data-id="element-2663">
                  <Briefcase size={18} data-id="element-2664" />
                </div>
                <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2665">
                  {attivi.length}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2666">
                  Attivi
                </div>
              </CardContent>
            </Card>
            <Card data-id="element-2667">
              <CardContent className="text-center" data-id="element-2668">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-2" data-id="element-2669">
                  <CheckCircle2 size={18} data-id="element-2670" />
                </div>
                <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2671">
                  {completati.length}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2672">
                  Completati
                </div>
              </CardContent>
            </Card>
            <Card data-id="element-2673">
              <CardContent className="text-center" data-id="element-2674">
                <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center mx-auto mb-2" data-id="element-2675">
                  <Star size={18} data-id="element-2676" />
                </div>
                <div className="text-2xl font-bold text-navy leading-tight" data-id="element-2677">
                  {avg ?? '—'}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5" data-id="element-2678">
                  Score medio
                </div>
              </CardContent>
            </Card>
          </div>
          <Card data-id="element-2679">
            <CardContent className="space-y-0 divide-y divide-border" data-id="element-2680">
              <StatRow label="Incarichi totali" value={String(jobsCleaner.length)} data-id="element-2681" />
              <StatRow label="Guadagno completati" value={`€${completati.reduce((s, j) => s + j.earning, 0)}`} data-id="element-2682" />
              <StatRow label="Tasso completamento" value={jobsCleaner.length > 0 ? `${Math.round(completati.length / jobsCleaner.length * 100)}%` : '—'} data-id="element-2683" />
            </CardContent>
          </Card>
        </div>}

      {/* Assign modal */}
      {isAssignOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={() => setIsAssignOpen(false)} data-id="element-2684">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Assegna opportunità" data-id="element-2685">
            <div className="flex items-start justify-between p-4 border-b border-border" data-id="element-2686">
              <div data-id="element-2687">
                <h2 className="text-lg font-bold text-navy" data-id="element-2688">
                  Assegna a {cleaner.name}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2689">
                  Opportunità accettate da assegnare, in base alle zone coperte.
                </p>
              </div>
              <button onClick={() => setIsAssignOpen(false)} aria-label="Chiudi" className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0" data-id="element-2690">
                <X size={20} className="text-gray-500" data-id="element-2691" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-2692">
              {openJobs.length === 0 ? <div className="text-center py-8" data-id="element-2693">
                  <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-2694">
                    <Briefcase size={18} className="text-muted-foreground" data-id="element-2695" />
                  </div>
                  <p className="text-sm font-medium text-foreground" data-id="element-2696">
                    Nessuna opportunità da assegnare
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-id="element-2697">
                    Tutti gli incarichi accettati sono già assegnati.
                  </p>
                </div> : <>
                  {assignable.length > 0 && <div data-id="element-2698">
                      <h3 className="text-xs font-medium text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1.5" data-id="element-2699">
                        <MapPin size={12} data-id="element-2700" />
                        Nelle zone di {cleaner.name.split(' ')[0]} (
                        {assignable.length})
                      </h3>
                      <div className="space-y-2" data-id="element-2701">
                        {assignable.map(j => <AssignableRow key={j.id} job={j} onAssign={() => handleAssign(j.id)} data-id="element-2702" />)}
                      </div>
                    </div>}
                  {otherOpen.length > 0 && <div data-id="element-2703">
                      <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2" data-id="element-2704">
                        Altre opportunità ({otherOpen.length})
                      </h3>
                      <div className="space-y-2" data-id="element-2705">
                        {otherOpen.map(j => <AssignableRow key={j.id} job={j} onAssign={() => handleAssign(j.id)} data-id="element-2706" />)}
                      </div>
                    </div>}
                </>}
            </div>
            <div className="p-4 border-t border-border bg-gray-50 flex justify-end" data-id="element-2707">
              <Button variant="subtle" onClick={() => setIsAssignOpen(false)} data-id="element-2708">
                Chiudi
              </Button>
            </div>
          </div>
        </div>}
    </MobileLayout>;
}
function InfoRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0 gap-4" data-id="element-2709">
      <span className="text-sm text-muted-foreground" data-id="element-2710">{label}</span>
      <span className="text-sm font-medium text-navy text-right truncate" data-id="element-2711">
        {value}
      </span>
    </div>;
}
function StatRow({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex items-center justify-between py-3 first:pt-0 last:pb-0" data-id="element-2712">
      <span className="text-sm text-muted-foreground" data-id="element-2713">{label}</span>
      <span className="text-sm font-semibold text-navy" data-id="element-2714">{value}</span>
    </div>;
}
function AssignableRow({
  job,
  onAssign
}: {
  job: ReturnType<typeof usePlatform>['jobs'][number];
  onAssign: () => void;
}) {
  const [assigned, setAssigned] = useState(false);
  return <div className="flex items-center justify-between gap-3 p-3 border border-border rounded-xl" data-id="element-2715">
      <div className="min-w-0" data-id="element-2716">
        <div className="text-sm font-semibold text-foreground truncate" data-id="element-2717">
          {job.apartmentName}
        </div>
        <div className="flex items-center gap-2.5 text-xs text-muted-foreground mt-0.5 flex-wrap" data-id="element-2718">
          <span className="flex items-center gap-1" data-id="element-2719">
            <MapPin size={11} data-id="element-2720" />
            {job.zone}
          </span>
          <span className="flex items-center gap-1" data-id="element-2721">
            <Clock size={11} data-id="element-2722" />
            {job.date} · {job.time}
          </span>
          <span data-id="element-2723">€{job.earning}</span>
        </div>
      </div>
      {assigned ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 flex-shrink-0" data-id="element-2724">
          <Check size={14} data-id="element-2725" />
          Inviata
        </span> : <Button size="sm" variant="outline" className="flex-shrink-0" onClick={() => {
      onAssign();
      setAssigned(true);
    }} data-id="element-2726">
          Invia richiesta
        </Button>}
    </div>;
}